/**
 * Unit-selection schedule: timetable structure + instance grid for unit courses.
 * Pure helpers (no React).
 */

/** Column order and day_of_week index: 0 = Monday … 6 = Sunday */
export const UNIT_SELECTION_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Add minutes to HH:mm start time (same calendar day, wraps within 24h). */
export function addMinutesToHmTime(startStr, addMins) {
  const parts = String(startStr || "09:00").split(":");
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  const base = (Number.isFinite(hh) ? hh : 9) * 60 + (Number.isFinite(mm) ? mm : 0);
  const a = base + (Number(addMins) || 0);
  const h = Math.floor(a / 60) % 24;
  const mi = a % 60;
  return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
}

export const SCHEDULE_TYPE_UNIT_SELECTION = "unit_selection";

/** @param {object} course with session_components */
export function defaultUnitStructureRows(course) {
  const comps = course?.session_components || [];
  return comps.map((_, i) => ({
    component_index: i,
    week_start: 1,
    week_end: 1,
    times_per_week: 1,
    hours_per_instance: 1,
    minutes_per_instance: 60,
    lecturer_id: "",
  }));
}

function compDurationMinutes(comp) {
  if (!comp) return null;
  const dm = comp.duration_minutes ?? comp.durationMinutes;
  const n = Number(dm);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function rowNumeric(v) {
  if (v === null || v === undefined || v === "") return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/** Expected total minutes from structure row vs course definition. */
export function structureRowTargetMinutes(row) {
  const ws = rowNumeric(row.week_start);
  const we = rowNumeric(row.week_end);
  const t = rowNumeric(row.times_per_week);
  const m = rowNumeric(row.minutes_per_instance);
  if (![ws, we, t, m].every(Number.isFinite)) return 0;
  const w = Math.max(0, we - ws + 1);
  if (w <= 0 || t <= 0 || m <= 0) return 0;
  return Math.round(w * t * m);
}

/**
 * @param {object} course
 * @param {number} totalWeeks
 * @param {Array} rows from defaultUnitStructureRows shape
 * @returns {{ component_index: number | null, message: string }[]} component_index null = whole form / course
 */
export function validateUnitStructure(course, totalWeeks, rows) {
  const errs = [];
  const comps = course?.session_components || [];
  const tw = Math.max(1, parseInt(String(totalWeeks == null || totalWeeks === "" ? "" : totalWeeks), 10) || 1);
  if (!comps.length) {
    errs.push({ component_index: null, message: "This course has no unit session types defined. Choose another course or fix the program." });
    return errs;
  }
  rows.forEach((row) => {
    const ci = row.component_index;
    const comp = comps[ci];
    const required = compDurationMinutes(comp);
    const got = structureRowTargetMinutes(row);
    const name = comp?.session_type || comp?.sessionType || `Session ${ci + 1}`;
    if (required != null && Math.round(got) !== Math.round(required)) {
      errs.push({
        component_index: Number.isInteger(ci) ? ci : null,
        message: `${name}: planned total is ${got} min but the course requires ${required} min. Adjust first/last week, sessions per week, or hours and minutes per class so they multiply to the target.`,
      });
    }
    const ws = parseInt(String(row.week_start == null ? "" : row.week_start), 10);
    const we = parseInt(String(row.week_end == null ? "" : row.week_end), 10);
    if (!Number.isInteger(ws) || !Number.isInteger(we) || ws < 1 || we < ws || we > tw) {
      errs.push({
        component_index: Number.isInteger(ci) ? ci : null,
        message: `${name}: week range must stay within 1 and ${tw} (first week ≤ last week, both ≤ unit length).`,
      });
    }
  });
  return errs;
}

function parseLocalDayMinutes(ymd, timeStr) {
  if (!ymd || !timeStr) return null;
  const t = String(timeStr).trim();
  const d = new Date(`${ymd}T${t.length === 5 ? `${t}:00` : t}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

/** Minutes for one instance (same calendar day). */
export function instanceSlotMinutes(inst, week1MondayYmd) {
  const ymd = dateForWeekDaySlot(week1MondayYmd, inst.week_number, inst.day_of_week);
  if (!ymd) return 0;
  const a = parseLocalDayMinutes(ymd, inst.start_time);
  const b = parseLocalDayMinutes(ymd, inst.end_time);
  if (a == null || b == null || b <= a) return 0;
  return Math.round(b - a);
}

export function totalInstanceMinutesForComponent(instances, componentIndex, week1MondayYmd) {
  let sum = 0;
  for (const inst of instances || []) {
    if (parseInt(String(inst.component_index), 10) !== componentIndex) continue;
    sum += instanceSlotMinutes(inst, week1MondayYmd);
  }
  return Math.round(sum);
}

/**
 * Validate instance step against timetable structure:
 * - each instance duration must equal row.minutes_per_instance
 * - each week in row range must have at least row.times_per_week instances
 * @returns {string[]} errors
 */
export function validateUnitInstancesAgainstStructureRows(course, structureRows, instances, week1MondayYmd) {
  const errs = [];
  const comps = course?.session_components || [];
  if (!week1MondayYmd || String(week1MondayYmd).trim().length < 10) {
    errs.push("Choose the Monday that starts week 1 of the unit.");
    return errs;
  }
  const rows = Array.isArray(structureRows) ? structureRows : [];
  rows.forEach((row) => {
    const ci = parseInt(String(row.component_index), 10);
    if (!Number.isInteger(ci) || ci < 0 || ci >= comps.length) return;
    const name = comps[ci]?.session_type || comps[ci]?.sessionType || `Session ${ci + 1}`;
    const ws = Math.max(1, parseInt(String(row.week_start), 10) || 1);
    const we = Math.max(ws, parseInt(String(row.week_end), 10) || ws);
    const minPerWeek = Math.max(1, parseInt(String(row.times_per_week), 10) || 1);
    const minsPerInst = Math.max(1, parseInt(String(row.minutes_per_instance), 10) || 1);
    const rowInst = (instances || []).filter(
      (inst) => parseInt(String(inst.component_index), 10) === ci
    );

    rowInst.forEach((inst) => {
      const wk = parseInt(String(inst.week_number), 10);
      if (!Number.isInteger(wk) || wk < ws || wk > we) return;
      const got = instanceSlotMinutes(inst, week1MondayYmd);
      if (got !== minsPerInst) {
        errs.push(
          `${name}: each instance must be exactly ${minsPerInst} min (from structure). Found ${got} min in week ${wk}.`
        );
      }
    });

    for (let w = ws; w <= we; w += 1) {
      const count = rowInst.filter((inst) => parseInt(String(inst.week_number), 10) === w).length;
      if (count < minPerWeek) {
        errs.push(
          `${name}: week ${w} has ${count} session(s); minimum ${minPerWeek} per week is required.`
        );
      }
    }
  });
  return errs;
}

/**
 * @returns {string[]} errors
 */
export function validateUnitInstancesAgainstCourse(course, instances, week1MondayYmd) {
  const errs = [];
  const comps = course?.session_components || [];
  if (!week1MondayYmd || String(week1MondayYmd).trim().length < 10) {
    errs.push("Choose the Monday that starts week 1 of the unit.");
    return errs;
  }
  comps.forEach((comp, j) => {
    const req = compDurationMinutes(comp);
    if (req == null) return;
    const got = totalInstanceMinutesForComponent(instances, j, week1MondayYmd);
    const name = comp?.session_type || comp?.sessionType || `Session ${j + 1}`;
    if (Math.round(got) < Math.round(req)) {
      errs.push(
        `${name}: scheduled instances total ${got} min; minimum required is ${req} min. Add more instances or increase duration.`
      );
    }
  });
  return errs;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatYMD(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Old wizard used the Sunday that began week 1. New model uses the Monday that begins week 1
 * (same teaching week Mon–Sun as before when week started Sunday, except the Sunday-only offset).
 */
export function legacyWeek1SundayToMondayYmd(sundayYmd) {
  const s = String(sundayYmd || "").trim();
  if (s.length < 10) return "";
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 1);
  return formatYMD(d);
}

/**
 * week_number 1-based; day_of_week 0 = Monday … 6 = Sunday.
 * week1MondayYmd is the calendar date of the Monday that begins week 1.
 */
export function dateForWeekDaySlot(week1MondayYmd, weekNumber, dayOfWeekMon0Sun6) {
  const base = new Date(`${String(week1MondayYmd).trim()}T12:00:00`);
  if (Number.isNaN(base.getTime())) return "";
  const w = Math.max(1, parseInt(String(weekNumber), 10) || 1);
  const d0 = parseInt(String(dayOfWeekMon0Sun6), 10);
  const day = Number.isInteger(d0) && d0 >= 0 && d0 <= 6 ? d0 : 0;
  const dt = new Date(base);
  dt.setDate(dt.getDate() + (w - 1) * 7 + day);
  return formatYMD(dt);
}

/** local datetime string to ISO slice (same as wizard). */
export function localDatetimeToIsoUtc(localValue) {
  if (!localValue || typeof localValue !== "string") return "";
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 19);
}

/**
 * Build API scheduled_slots from unit instances (times + optional room/lecturer/component index).
 */
export function buildScheduledSlotsForUnitSelection(week1MondayYmd, instances) {
  const out = [];
  for (const inst of instances || []) {
    const ymd = dateForWeekDaySlot(week1MondayYmd, inst.week_number, inst.day_of_week);
    if (!ymd) continue;
    const st = String(inst.start_time || "").trim();
    const en = String(inst.end_time || "").trim();
    if (!st || !en) continue;
    const stNorm = st.length === 5 ? st : st.slice(0, 5);
    const enNorm = en.length === 5 ? en : en.slice(0, 5);
    const startIso = localDatetimeToIsoUtc(`${ymd}T${stNorm}`);
    const endIso = localDatetimeToIsoUtc(`${ymd}T${enNorm}`);
    if (!startIso || !endIso) continue;
    const lid = inst.lecturer_id != null && String(inst.lecturer_id).trim() !== ""
      ? Number(inst.lecturer_id)
      : null;
    const ci = parseInt(String(inst.component_index), 10);
    const row = {
      start_datetime: startIso,
      end_datetime: endIso,
      unit_component_index: Number.isInteger(ci) ? ci : null,
    };
    const online = Boolean(inst.is_online);
    const linkTrim = String(inst.slot_meeting_link || "").trim();
    if (online) {
      row.is_online = true;
      if (linkTrim) row.slot_meeting_link = linkTrim;
    } else {
      const cid = inst.classroom_id != null && String(inst.classroom_id).trim() !== ""
        ? Number(inst.classroom_id)
        : null;
      if (cid != null && !Number.isNaN(cid)) row.classroom_id = cid;
      if (Boolean(inst.hybrid_online_enabled) && linkTrim) {
        row.slot_meeting_link = linkTrim;
      }
    }
    if (lid != null && !Number.isNaN(lid)) row.lecturer_id = lid;
    const capRaw = inst.slot_capacity;
    if (capRaw != null && String(capRaw).trim() !== "") {
      const capN = Number(capRaw);
      if (!Number.isNaN(capN) && capN >= 1) row.slot_capacity = capN;
    }
    out.push(row);
  }
  return out.sort((a, b) => String(a.start_datetime).localeCompare(String(b.start_datetime)));
}
